import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {EmbeddedPage} from '@tinct/core-api-models';
import {useJobAlert} from '@tinct/web-layout';
import {getPageDisplayIdentity} from '../../../pages/shared/get-page-display-identity';
import {useStudioHostUrls} from '../studio-host-context';

/**
 * "Let it run in background" for the studio's generating overlay: hands the
 * selected page's generation job to the background-jobs widget (which polls it
 * and links back to this variant once done) and leaves the studio. No top
 * alert bar: the user is navigated away on purpose, the floating widget is
 * the follow-up. The backend already emails when a generate-all completes.
 */
export function useBackgroundGenerationJobAlert(campaignId: string, page: EmbeddedPage | null) {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  // The host decides where this studio lives and where leaving it lands
  // (funnel preview step → dashboard, management studio → campaign).
  const {studioUrl, campaignUrl} = useStudioHostUrls(campaignId);
  const label = page ? getPageDisplayIdentity(page).label : 'page';

  const {start} = useJobAlert({
    name: `Generating ${label}`,
    pendingUrl: pathname,
    // The `?pageId=` deep link reopens the variants tab on this page.
    completedUrl: page?.id ? `${studioUrl}?pageId=${page.id}` : studioUrl,
  });

  const runInBackground = useCallback(() => {
    const jobId = page?.lastGenerationJob?.id;
    if (jobId) start(jobId);
    navigate(campaignUrl);
  }, [page?.lastGenerationJob?.id, start, navigate, campaignUrl]);

  return {runInBackground};
}
